import React from "react";
import { Box } from "@mui/material";
import SabiasQue from "../SabiasQue";

// Template30: Página Día 2 - Ninna-ji, Castillo Nijō, Mercado Nishiki y Pontochō (Kioto)
// Contenido desde page.texts, page.images y page.canvasImages (servidor).
const Template30 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const isSmallScreen = window.innerWidth <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch) return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};

	const headerText = page?.texts?.[0]?.content;
	const ninnaTitle = page?.texts?.[1]?.content;
	const ninnaText = page?.texts?.[2]?.content;
	const ninnaTransporteText = page?.texts?.[3]?.content;
	const nijoTitle = page?.texts?.[4]?.content;
	const nijoText = page?.texts?.[5]?.content;
	const nijoTransporteTitle = page?.texts?.[6]?.content;
	const nijoTransporteText = page?.texts?.[7]?.content;
	const mercadoTitle = page?.texts?.[8]?.content;
	const mercadoText = page?.texts?.[9]?.content;
	const kawadokoText = page?.texts?.[10]?.content;
	const pontochoTitle = page?.texts?.[11]?.content;
	const pontochoText = page?.texts?.[12]?.content;
	const pontochoTransporteText = page?.texts?.[13]?.content;
	const sabiasQueNijo1 = parseSabiasQueItem(page?.texts?.[14]?.content);
	const sabiasQueNijo2 = parseSabiasQueItem(page?.texts?.[15]?.content);
	const sabiasQueMercado = parseSabiasQueItem(page?.texts?.[16]?.content);
	const sabiasQuePontocho = parseSabiasQueItem(page?.texts?.[17]?.content);

	const logoImage = getCanvasImageUrl("logo");
	const mercadoImage = getImageSrc("mercado");
	const nijoImage = getImageSrc("nijo");
	const pontochoCalleImage = getImageSrc("pontocho-calle");
	const pontochoSticker = getCanvasImageUrl("pontocho-sticker");

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
		padding: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "0px !important",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: headerBackgroundColor || "#5ba374",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 9999,
				}}
			>
				{headerText}
				{!isSmallScreen && logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			<Box
				data-block-id="text-1"
				sx={{
					padding: "10px 12px",
					display: "flex",
					flexDirection: "column",
					gap: "20px",
					marginTop: "8px",
				}}
			>
				{(ninnaTitle || ninnaText) && (
					<Box sx={{ display: "flex", gap: "16px", position: "relative" }}>
						<Box sx={{ position: "relative" }}>
							{ninnaTitle && (
								<Box component="h3" sx={h3}>
									{ninnaTitle}
								</Box>
							)}
							{ninnaText && (
								<Box
									component="p"
									sx={{
										...p,
										margin: "0 !important",
										marginBottom: "0px !important",
									}}
								>
									{ninnaText}
								</Box>
							)}
						</Box>
					</Box>
				)}

				{ninnaTransporteText && (
					<Box
						sx={{
							backgroundColor: "#d9e6de",
							padding: "12px",
							borderRadius: "12px",
							display: "flex",
							gap: "16px",
							alignItems: "center",
							position: "relative",
						}}
					>
						<Box sx={{ width: "100%" }}>
							<Box
								component="p"
								sx={{
									...p,
									margin: "0 !important",
									marginBottom: "0px !important",
								}}
							>
								{ninnaTransporteText}
							</Box>
						</Box>
					</Box>
				)}

				<Box sx={{ display: "flex", gap: "16px", position: "relative" }}>
					{nijoImage && (
						<Box
							component="img"
							src={nijoImage}
							alt="Nijo"
							sx={{
								width: "40%",
								height: 110,
								borderRadius: "12px",
								objectFit: "cover",
							}}
						/>
					)}
					{sabiasQueNijo1 && (
						<Box sx={{ position: "absolute", top: 0, left: "33%" }}>
							<SabiasQue
								icono={sabiasQueNijo1.icono}
								texto={sabiasQueNijo1.texto}
							/>
						</Box>
					)}
					{sabiasQueNijo2 && (
						<Box
							sx={{
								position: "absolute",
								bottom: isSmallScreen ? 0 : "-10px",
								left: "33%",
							}}
						>
							<SabiasQue
								icono={sabiasQueNijo2.icono}
								texto={sabiasQueNijo2.texto}
							/>
						</Box>
					)}
					<Box sx={{ width: "60%" }}>
						{nijoTitle && (
							<Box component="h3" sx={h3}>
								{nijoTitle}
							</Box>
						)}
						{nijoText && (
							<Box
								component="p"
								sx={{
									...p,
									margin: "0 !important",
									marginBottom: "0px !important",
								}}
							>
								{nijoText}
							</Box>
						)}
					</Box>
				</Box>

				{(nijoTransporteTitle || nijoTransporteText) && (
					<Box
						sx={{
							backgroundColor: "#d9e6de",
							padding: "12px",
							borderRadius: "12px",
							display: "flex",
							gap: "16px",
							alignItems: "center",
							position: "relative",
						}}
					>
						<Box sx={{ width: "100%" }}>
							{nijoTransporteTitle && (
								<Box component="h3" sx={h3}>
									{nijoTransporteTitle}
								</Box>
							)}
							{nijoTransporteText && (
								<Box
									component="p"
									sx={{
										...p,
										margin: "0 !important",
										marginBottom: "0px !important",
									}}
								>
									{nijoTransporteText}
								</Box>
							)}
						</Box>
					</Box>
				)}

				<Box
					sx={{
						display: "flex",
						gap: "16px",
						position: "relative",
						marginBottom: isSmallScreen ? "10px" : "10px",
					}}
				>
					<Box sx={{ width: "60%", position: "relative" }}>
						{mercadoTitle && (
							<Box component="h3" sx={h3}>
								{mercadoTitle}
							</Box>
						)}
						{mercadoText && (
							<Box
								component="p"
								sx={{
									...p,
									margin: "0 !important",
									marginBottom: "0px !important",
								}}
							>
								{mercadoText}
							</Box>
						)}
					</Box>
					{mercadoImage && (
						<Box
							component="img"
							src={mercadoImage}
							alt="Mercado"
							sx={{
								width: "40%",
								height: 110,
								borderRadius: "12px",
								objectFit: "cover",
							}}
						/>
					)}
					{sabiasQueMercado && (
						<Box sx={{ position: "absolute", bottom: "-10px", left: "50%" }}>
							<SabiasQue
								icono={sabiasQueMercado.icono}
								texto={sabiasQueMercado.texto}
							/>
						</Box>
					)}
					{!isSmallScreen && (
						<>
							{kawadokoText && (
								<Box
									sx={{
										position: "absolute",
										bottom: "-10px",
										left: "20px",
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: "16px 16px 16px 0px",
										padding: "10px 10px",
										fontSize: "11px",
										fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
										color: contentColor,
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: 230,
										zIndex: 6,
									}}
								>
									{kawadokoText}
								</Box>
							)}
							{pontochoSticker && (
								<Box
									component="img"
									src={pontochoSticker}
									alt="Pontocho"
									sx={{
										width: 100,
										position: "absolute",
										bottom: "-80px",
										left: "-30px",
										zIndex: 5,
									}}
								/>
							)}
						</>
					)}
				</Box>

				<Box sx={{ display: "flex", gap: "16px", position: "relative" }}>
					{pontochoCalleImage && (
						<Box
							component="img"
							src={pontochoCalleImage}
							alt="Pontocho"
							sx={{
								width: "40%",
								height: 110,
								borderRadius: "12px",
								objectFit: "cover",
							}}
						/>
					)}
					<Box
						sx={{
							width: "60%",
							gap: "12px",
							display: "flex",
							flexDirection: "column",
						}}
					>
						<Box>
							{pontochoTitle && (
								<Box component="h3" sx={h3}>
									{pontochoTitle}
								</Box>
							)}
							{pontochoText && (
								<Box
									component="p"
									sx={{
										...p,
										margin: "0 !important",
										marginBottom: "0px !important",
									}}
								>
									{pontochoText}
								</Box>
							)}
						</Box>

						{isSmallScreen && (
							<>
								{kawadokoText && (
									<Box
										sx={{
											position: "absolute",
											bottom: "20px",
											left: "20px",
											backgroundColor: "#fff8db",
											border: "2px solid #f5c400",
											borderRadius: "16px 16px 16px 0px",
											padding: "10px 10px",
											fontSize: "11px",
											fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
											color: contentColor,
											boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
											maxWidth: 130,
											zIndex: 6,
										}}
									>
										{kawadokoText}
									</Box>
								)}
								{pontochoSticker && (
									<Box
										component="img"
										src={pontochoSticker}
										alt="Pontocho"
										sx={{
											width: 100,
											position: "absolute",
											bottom: "-50px",
											left: "-30px",
											zIndex: 5,
										}}
									/>
								)}
							</>
						)}

						{pontochoTransporteText && (
							<Box
								sx={{
									backgroundColor: "#d9e6de",
									border: "3px solid #5ba374",
									borderRadius: "12px",
									padding: "8px 10px",
									fontSize: "12px",
									fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
									color: "#333",
								}}
							>
								<Box
									component="p"
									sx={{
										fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
										fontSize: "12px",
										color: contentColor,
										margin: "0 !important",
									}}
								>
									{pontochoTransporteText}
								</Box>
							</Box>
						)}
					</Box>

					{sabiasQuePontocho && (
						<Box
							sx={{
								position: "absolute",
								top: "40px",
								left: isSmallScreen ? "33%" : "35%",
							}}
						>
							<SabiasQue
								icono={sabiasQuePontocho.icono}
								texto={sabiasQuePontocho.texto}
							/>
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template30.thumbnail = "/images/template-1.png";

export default Template30;
